import { buildEventCardModel, getRowSettlementResult, resolveEventCardHref } from './model';
import { InteractCheckIcon, InteractWatchIcon } from '@/client/assets';
import { ApiTranslation } from '@/client/components/api-translation';
import { CurrencyView } from '@/client/components/currency-view';
import { FollowMarket } from '@/client/components/follow-market';
import { LocalizedText } from '@/client/components/localized-text';
import { MarketDeadline } from '@/client/components/market-deadline';
import { OptionButton } from '@/client/components/option-button';
import { ShareMarket } from '@/client/components/share-market';
import { TextTooltip } from '@/client/components/text-tooltip';
import { UploadedImage } from '@/client/components/uploaded-image';
import { prefetchRoute } from '@/client/confs/page-loaders';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { queryClient } from '@/client/utils/query-client';
import { buildEventDetailQueryOptions } from '@/libs/react-sdk/api/use-event-detail';
import { FollowStatus, type Market, MarketStatus } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { memo, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

// Category emoji mapping for fallback (shared with MarketCard)
const getCategoryEmoji = (category?: string): string => {
  const emojiMap: Record<string, string> = {
    crypto: '₿',
    politics: '🏛️',
    sports: '⚽',
    tech: '💻',
    entertainment: '🎬',
    economy: '📊',
    science: '🔬',
    culture: '🎭',
    world: '🌍',
    finance: '💰',
  };
  return emojiMap[category?.toLowerCase() || ''] || '📈';
};

type EventCardProps = {
  eventId: string;
  markets: Market[];
  className?: string;
  index?: number;
  /** Home grid 5-minute cell only: a LIVE chip leads the header while the round trades. */
  live?: boolean;
};

const EventCardComponent = ({ eventId, markets, className, index = 0, live }: EventCardProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  // Use eventTitle from market list API directly — no extra API call needed
  const title = markets[0]?.eventTitle || markets[0]?.marketName || '';
  // Companion i18n map for the card title: event_title_i18n when we used eventTitle,
  // else the lead market's name_i18n (mirrors the `||` fallback above).
  const titleI18n = markets[0]?.eventTitle ? markets[0]?.eventTitleI18n : markets[0]?.nameI18n;
  const model = useMemo(() => buildEventCardModel(markets, title), [markets, title]);
  const href = resolveEventCardHref(markets, eventId);

  // Warm the event route chunk + event-detail cache on intent signals so the
  // click-through transition commits without suspending. prefetchQuery
  // respects staleTime, so repeated hovers within it are free.
  const handlePrefetch = () => {
    prefetchRoute('event');
    if (eventId) void queryClient.prefetchQuery(buildEventDetailQueryOptions(api, { eventId }));
  };

  const isSingleMarket = markets.length === 1;

  // Single-market: stagger animation + image fallback
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Element state (not a ref) so the observer re-attaches when the node mounts
  // late — with a plain ref, a card that first renders null (model not yet
  // resolved) is never observed and stays at opacity 0 forever.
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);
  const [inViewport] = useInViewport(cardEl ?? undefined, { threshold: 0.1 });

  useEffect(() => {
    if (!isSingleMarket) return;
    if (inViewport && !isVisible) {
      const delay = Math.min(index % 8, 7) * 50;
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [inViewport, isVisible, index, isSingleMarket]);

  // Belt-and-braces reveal for any remaining observer miss: rect-check when
  // the element mounts (cardEl is state, so a late mount re-fires this).
  useEffect(() => {
    if (!isSingleMarket || isVisible) return;
    const el = cardEl;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      const delay = Math.min(index % 8, 7) * 50;
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [cardEl, isVisible, index, isSingleMarket]);

  // Single-market: pre-compute result option for settlement display
  const singleRow = model?.displayRows[0];
  const singleMarket = singleRow?.market;
  // Fallback option list for PredictionCTF contracts where on-chain options() reverts
  const singleApiOptionList = useMemo(
    () => singleMarket?.options.map(o => ({ optionAddress: o.tokenAddress })),
    [singleMarket?.options]
  );
  const resultOption = useMemo(() => {
    if (!isSingleMarket || !singleMarket) return undefined;
    return singleMarket.options.find(item => item.tokenAddress === singleMarket.result);
  }, [isSingleMarket, singleMarket]);

  if (!model) return null;

  const leadMarket = model.leadMarket;
  const picUrl = leadMarket.marketPicUrl;
  // 逐场盘(每队一个 Yes/No 市场): 卡片封面拼两面队旗 —— list 接口不返回 event 封面,
  // 卡片只能用各市场自己的图拼合 (恰好 2 个市场时生效; 3+ 市场如冠军盘保持原样)
  const duoPics = markets.length === 2 ? markets.slice(0, 2).map(m => m.marketPicUrl) : null;
  const categoryChip = leadMarket.categoryName && (
    <span className="neon-chip cat">
      <ApiTranslation>{leadMarket.categoryName}</ApiTranslation>
    </span>
  );

  return (
    <div
      ref={setCardEl}
      className={cn(
        // mcard-neon: animated diagonal streaks + sheen sweep + neon hover (design handoff)
        // mcard-surface: translucent gold-tinted "inside colour" gradient
        'mcard-neon mcard-surface group p-5 w-full flex flex-col gap-3.5 cursor-pointer',
        'rounded-2xl',
        'transition-all duration-300 ease-out',
        // Single-market: fade-in stagger animation (multi-market rows always painted)
        isSingleMarket ? (isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3') : 'opacity-100 translate-y-0',
        isSingleMarket && isVisible && 'mcard-reveal-glow in',
        className
      )}
      onClick={() => navigate(href)}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onTouchStart={handlePrefetch}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 size-10 rounded-lg overflow-hidden bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center relative border border-border">
          {isSingleMarket ? (
            // Single-market: emoji fallback on image error
            picUrl && !imageError ? (
              <UploadedImage
                fileKey={picUrl}
                className="size-full object-cover absolute inset-0"
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="text-base">{getCategoryEmoji(leadMarket.categoryName)}</span>
            )
          ) : duoPics ? (
            <div className="absolute inset-0 flex">
              {duoPics.map((p, i) => (
                <UploadedImage key={i} fileKey={p} className="w-1/2 h-full object-cover" />
              ))}
            </div>
          ) : (
            <UploadedImage fileKey={picUrl} className="size-full object-cover absolute inset-0" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          {live ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="neon-chip live">{t('live')}</span>
              {categoryChip}
            </div>
          ) : (
            categoryChip
          )}
          <TextTooltip tooltip={model.title} className="neon-q line-clamp-2">
            <LocalizedText i18nMap={titleI18n} fallback={model.title} />
          </TextTooltip>
        </div>
      </div>

      {isSingleMarket && singleRow ? (
        <>
          {/* Single-market: MarketCard-style trading section */}
          {singleMarket!.marketStatus == MarketStatus.progress && (
            <div className="space-y-3">
              <div className="neon-prob">
                <div className="pct" style={{ color: singleRow.probability >= 50 ? 'var(--neon-yes)' : 'var(--neon-no)' }}>
                  {singleRow.probability}%<small>{t('chance')?.toUpperCase()}</small>
                </div>
                <div className="neon-bar">
                  <i style={{ width: `${singleRow.probability}%` }} />
                </div>
              </div>
              <div className="flex gap-2">
                <OptionButton
                  marketAddress={singleRow.market.marketAddress}
                  marketDecimal={singleRow.yes.decimal}
                  optionAddress={singleRow.yes.tokenAddress}
                  optionName={singleRow.yes.tokenName}
                  optionDescription={singleRow.yes.tokenDescription}
                  optionPrice={Number(singleRow.yes.tokenPrice)}
                  optionDecimal={singleRow.yes.decimal}
                  optionSide="yes"
                  apiOptionList={singleApiOptionList}
                  navigateTo={href}
                  className="flex-1 h-8 py-0 rounded-md font-medium text-sm"
                >
                  <ApiTranslation>{singleRow.yes.tokenDescription || 'Yes'}</ApiTranslation>
                </OptionButton>
                <OptionButton
                  marketAddress={singleRow.market.marketAddress}
                  marketDecimal={singleRow.no.decimal}
                  optionAddress={singleRow.no.tokenAddress}
                  optionName={singleRow.no.tokenName}
                  optionDescription={singleRow.no.tokenDescription}
                  optionPrice={Number(singleRow.no.tokenPrice)}
                  optionDecimal={singleRow.no.decimal}
                  optionSide="no"
                  apiOptionList={singleApiOptionList}
                  navigateTo={href}
                  className="flex-1 h-8 py-0 rounded-md font-medium text-sm"
                >
                  <ApiTranslation>{singleRow.no.tokenDescription || 'No'}</ApiTranslation>
                </OptionButton>
              </div>
            </div>
          )}

          {/* Single-market: settlement result */}
          {singleMarket!.marketStatus >= MarketStatus.settlement && resultOption && (
            <div
              className={cn(
                'mt-3 text-xs font-medium py-1.5 px-2 rounded-md',
                resultOption.tokenAddress === singleRow.yes.tokenAddress
                  ? 'text-emerald-600 bg-emerald-500/10'
                  : 'text-red-500 bg-red-500/10'
              )}
            >
              {t('results-out-label')} {resultOption.tokenDescription}
            </div>
          )}
        </>
      ) : (
        /* Multi-market: compact row style (DPIN's primary card shape — price
           ranges, AI Race, weekly fixtures — visual anchor is here, not the
           binary chance% card). Rows carry their own mini Yes/No buttons,
           tabular-mono %, and a settlement winner chip once resolved. */
        <div className="space-y-2">
          {model.displayRows.length > 0 ? (
            model.displayRows.map(row => {
              const { market, yes, no, probability } = row;
              // Settled rows show the winning option instead of live Yes/No
              // buttons. A Yes win (the team that took the title) goes green,
              // every No row is muted so the actual winner stands out.
              const settlement = getRowSettlementResult(row);
              return (
                <div key={market.marketAddress} className="flex items-center justify-between gap-2">
                  <span
                    className={cn(
                      'truncate flex-1 text-xs',
                      settlement && !settlement.isYes ? 'text-muted-foreground' : 'text-foreground/85'
                    )}
                  >
                    <LocalizedText i18nMap={market.nameI18n} fallback={market.marketName} />
                  </span>
                  {/* Settled: only the winner gets a chip (✓ 獲勝); losing rows
                      drop to a muted name with nothing on the right, so the actual
                      winner is the only thing that pops. Live rows keep buttons. */}
                  {!settlement ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-mono text-xs font-semibold" style={{ color: 'var(--neon-yes)' }}>
                        {probability}%
                      </span>
                      <OptionButton
                        marketAddress={market.marketAddress}
                        marketDecimal={yes.decimal}
                        optionAddress={yes.tokenAddress}
                        optionName={yes.tokenName}
                        optionDescription={yes.tokenDescription}
                        optionPrice={Number(yes.tokenPrice)}
                        optionDecimal={yes.decimal}
                        optionSide="yes"
                        showDescription={false}
                        apiOptionList={market.options.map(o => ({ optionAddress: o.tokenAddress }))}
                        navigateTo={href}
                        className="h-5 px-1.5 py-0 rounded text-[10px] font-medium"
                      >
                        <ApiTranslation>{yes.tokenDescription || 'YES'}</ApiTranslation>
                      </OptionButton>
                      <OptionButton
                        marketAddress={market.marketAddress}
                        marketDecimal={no.decimal}
                        optionAddress={no.tokenAddress}
                        optionName={no.tokenName}
                        optionDescription={no.tokenDescription}
                        optionPrice={Number(no.tokenPrice)}
                        optionDecimal={no.decimal}
                        optionSide="no"
                        showDescription={false}
                        apiOptionList={market.options.map(o => ({ optionAddress: o.tokenAddress }))}
                        navigateTo={href}
                        className="h-5 px-1.5 py-0 rounded text-[10px] font-medium"
                      >
                        <ApiTranslation>{no.tokenDescription || 'NO'}</ApiTranslation>
                      </OptionButton>
                    </div>
                  ) : settlement.isYes ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium py-0.5 px-1.5 rounded text-emerald-600 bg-emerald-500/10">
                      <InteractCheckIcon className="size-2.5 fill-current shrink-0" />
                      {t('row-winner-label')}
                    </span>
                  ) : null}
                </div>
              );
            })
          ) : (
            <button
              className="w-full rounded-lg border border-border px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => navigate(href)}
            >
              {t('view-market', 'View market details')}
            </button>
          )}
          {model.displayRows.length > 0 && model.remainingCount > 0 && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => navigate(href)}
            >
              +{model.remainingCount} {t('n-more', 'more')}
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="neon-meta mt-auto pt-3">
        <CurrencyView
          className="font-mono whitespace-nowrap"
          value={model.totalVolume}
          decimal={leadMarket.marketDecimal}
          baseTokenAddress={leadMarket.baseTokenAddress}
          showIcon
          showUnit
          unitClassName="text-[10px]"
        >
          {t('vol')}
        </CurrencyView>
        <div className="flex items-center gap-2 ml-auto">
          {model.nearestDeadline && (
            <MarketDeadline deadline={model.nearestDeadline} status={leadMarket.marketStatus} className="text-[11px]" />
          )}
          <FollowMarket
            variant="ghost"
            iconClassName="p-0"
            className="size-3.5 hover:text-foreground transition-colors"
            watchIcon={<InteractWatchIcon className="size-3.5 fill-current" />}
            isFollowed={leadMarket.isFollowed == FollowStatus.yes}
            address={leadMarket.marketAddress}
          />
          <ShareMarket
            variant="ghost"
            className="size-3.5 hover:text-foreground transition-colors"
            iconClassName="p-0"
            marketName={leadMarket.marketName}
            marketAddress={leadMarket.marketAddress}
          />
        </div>
      </div>
    </div>
  );
};

export const EventCard = memo(EventCardComponent);
EventCard.displayName = 'EventCard';
